import { useEffect, useState } from "react";
import type { RecipientPickerUser } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { X, Send, Search, CheckCircle2 } from "lucide-react";

interface DirectMessageDialogProps {
  schoolId: string;
  schoolName: string;
  onClose: () => void;
  // 097 -- pre-select recipients when opened from a contextual CTA
  // (e.g. volunteer offer directory). Users must still be in the
  // school's recipient list for the selection to stick.
  initialRecipientIds?: string[];
  initialSubject?: string;
}

export function DirectMessageDialog({
  schoolId,
  schoolName,
  onClose,
  initialRecipientIds,
  initialSubject,
}: DirectMessageDialogProps) {
  const [recipients, setRecipients] = useState<RecipientPickerUser[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(initialRecipientIds ?? []),
  );
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<string | null>(null);
  const [content, setContent] = useState(
    initialSubject ? `${initialSubject}\n\n` : "",
  );
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    api
      .getSchoolRecipients(schoolId)
      .then((res) => setRecipients(res.recipients))
      .catch(() => setError("Failed to load recipients"))
      .finally(() => setLoading(false));
  }, [schoolId]);

  function toggleRecipient(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const filtered = recipients.filter((r) => {
    const name = [r.first_name, r.last_name].filter(Boolean).join(" ").toLowerCase();
    const matchesSearch = !search || name.includes(search.toLowerCase()) || r.email.toLowerCase().includes(search.toLowerCase());
    const matchesRole = !roleFilter || r.roles.includes(roleFilter);
    return matchesSearch && matchesRole;
  });

  const availableRoles = [...new Set(recipients.flatMap((r) => r.roles))].sort();

  async function handleSend() {
    if (!content.trim() || selectedIds.size === 0) return;
    setSending(true);
    setError(null);
    try {
      await api.sendDirectMessage({
        recipient_ids: Array.from(selectedIds),
        content: content.trim(),
        school_id: schoolId,
      });
      setSent(true);
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to send message");
    } finally {
      setSending(false);
    }
  }

  if (sent) {
    return (
      <div
        className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center overflow-y-auto p-4 sm:items-start sm:justify-end"
        data-dialog="direct-message"
        data-state="sent"
      >
        <Card className="w-full max-w-md mt-16 mr-2">
          <CardContent className="pt-6 flex flex-col items-center gap-4">
            <CheckCircle2 className="h-12 w-12 text-green-500" />
            <p className="text-lg font-medium">Message sent!</p>
            <p className="text-sm text-muted-foreground" data-region="sent-count">
              Sent to {selectedIds.size} recipient{selectedIds.size !== 1 ? "s" : ""}
            </p>
            <Button onClick={onClose} data-action="close">Close</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center overflow-y-auto p-4 sm:items-start sm:justify-end"
      data-dialog="direct-message"
      data-state={loading ? "loading" : sending ? "sending" : error ? "error" : "drafting"}
    >
      <Card className="w-full max-w-md max-h-[90vh] flex flex-col sm:mt-16 sm:mr-2">
        <CardHeader className="flex-shrink-0">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Send className="h-4 w-4" />
              Direct Message
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={onClose} data-action="close">
              <X className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-sm text-muted-foreground" data-region="school-label">{schoolName}</p>
        </CardHeader>
        <CardContent className="flex flex-col gap-4 overflow-hidden">
          {error && <p className="text-sm text-destructive" data-error>{error}</p>}

          {/* Search + role filter */}
          <div className="flex flex-col gap-2" data-region="recipient-picker">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-md border border-input bg-background pl-8 pr-3 py-2 text-sm"
                data-field="recipient-search"
              />
            </div>
            <div className="flex gap-1 flex-wrap">
              <Button
                variant={roleFilter === null ? "default" : "outline"}
                size="sm"
                onClick={() => setRoleFilter(null)}
                className="text-xs h-7"
              >
                All
              </Button>
              {availableRoles.map((role) => (
                <Button
                  key={role}
                  variant={roleFilter === role ? "default" : "outline"}
                  size="sm"
                  onClick={() => setRoleFilter(role)}
                  className="text-xs h-7"
                >
                  {role.replace("_", " ")}
                </Button>
              ))}
            </div>
          </div>

          {/* Recipient list */}
          <div className="overflow-y-auto max-h-48 border rounded-md">
            {loading && (
              <p className="text-sm text-muted-foreground p-3">Loading...</p>
            )}
            {!loading && filtered.length === 0 && (
              <p className="text-sm text-muted-foreground p-3">No recipients found</p>
            )}
            {filtered.map((r) => {
              const name = [r.first_name, r.last_name].filter(Boolean).join(" ") || r.email;
              return (
                <label
                  key={r.id}
                  className="flex items-center gap-2 px-3 py-2 hover:bg-muted cursor-pointer border-b last:border-b-0 text-sm"
                  data-item="recipient"
                  data-item-id={r.id}
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.has(r.id)}
                    onChange={() => toggleRecipient(r.id)}
                    className="rounded"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="truncate">{name}</p>
                    <p className="text-xs text-muted-foreground truncate">{r.email}</p>
                  </div>
                  <div className="flex gap-1">
                    {r.roles.map((role) => (
                      <span
                        key={role}
                        className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground"
                      >
                        {role.replace("_", " ")}
                      </span>
                    ))}
                  </div>
                </label>
              );
            })}
          </div>
          {selectedIds.size > 0 && (
            <p className="text-xs text-muted-foreground" data-region="selected-count" data-recipient-count={selectedIds.size}>
              {selectedIds.size} recipient{selectedIds.size !== 1 ? "s" : ""} selected
            </p>
          )}

          {/* Message content */}
          <div data-region="message-content">
            <label className="text-sm font-medium mb-1 block">Message</label>
            <textarea
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[80px] resize-y"
              placeholder="Type your message..."
              value={content}
              onChange={(e) => setContent(e.target.value)}
              maxLength={1000}
              data-field="content"
            />
            <p className="text-xs text-muted-foreground text-right">
              {content.length}/1000
            </p>
          </div>

          {/* Send button */}
          <Button
            onClick={handleSend}
            disabled={!content.trim() || selectedIds.size === 0 || sending}
            data-action="send"
          >
            <Send className="mr-2 h-4 w-4" />
            {sending ? "Sending..." : `Send to ${selectedIds.size} recipient${selectedIds.size !== 1 ? "s" : ""}`}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
